import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { OUTGOING_CATEGORIES, UPLOAD_BASE, beYearOptions, currentBeYear, fmtDateTH } from './aoShared'
import ThaiDateInput from '../components/ThaiDateInput'

const emptyForm = {
  out_doc_date: '', out_to: '', out_subject: '', out_category: 'ทั่วไป',
  out_signer: '', out_body: '', out_detail: '',
}

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }, { indent: '-1' }, { indent: '+1' }],
    ['clean'],
  ],
}

export default function OutgoingDocs() {
  const [year, setYear] = useState(String(currentBeYear()))
  const [category, setCategory] = useState('')
  const [q, setQ] = useState('')
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (year) params.set('year', year)
      if (category) params.set('category', category)
      if (q) params.set('q', q)
      const res = await axiosInstance.get(`/api/ao/outgoing?${params.toString()}`)
      if (res.data.success) setDocs(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year, category, q])

  useEffect(() => { fetchData() }, [fetchData])

  const openCreate = () => {
    setEditId(null)
    setForm({ ...emptyForm, out_doc_date: new Date().toLocaleDateString('en-CA') })
    setFile(null)
    setModalError('')
    setShowModal(true)
  }

  const openEdit = async (d) => {
    setModalError('')
    try {
      const res = await axiosInstance.get(`/api/ao/outgoing/${d.out_id}`)
      if (res.data.success) {
        const doc = res.data.data
        setEditId(doc.out_id)
        setForm({
          out_doc_date: doc.out_doc_date || '',
          out_to: doc.out_to || '',
          out_subject: doc.out_subject || '',
          out_category: doc.out_category || 'ทั่วไป',
          out_signer: doc.out_signer || '',
          out_body: doc.out_body || '',
          out_detail: doc.out_detail || '',
        })
        setFile(null)
        setShowModal(true)
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'โหลดข้อมูลไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''))
      if (file) fd.append('file', file)
      const res = editId
        ? await axiosInstance.put(`/api/ao/outgoing/${editId}`, fd)
        : await axiosInstance.post('/api/ao/outgoing', fd)
      if (res.data.success) {
        setShowModal(false)
        if (!editId) {
          Swal.fire({ icon: 'success', title: `ออกเลขส่ง ${res.data.data.out_full_no} เรียบร้อย`, timer: 2000, showConfirmButton: false })
        }
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-send-plane-line me-2 text-primary" />
              ทะเบียนหนังสือส่ง
            </h4>
            <Button variant="primary" size="sm" onClick={openCreate}>
              <i className="ri-add-line me-1" />ออกหนังสือส่ง
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
              </Form.Select>
              <Form.Select size="sm" style={{ width: 180 }} value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">ทุกประเภท</option>
                {OUTGOING_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
              <Form.Control size="sm" style={{ maxWidth: 240 }} placeholder="ค้นหา เลขส่ง/ถึง/เรื่อง"
                value={q} onChange={e => setQ(e.target.value)} />
              <div className="ms-auto text-muted fs-sm">{docs.length} รายการ</div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : docs.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบหนังสือส่งในเงื่อนไขที่เลือก</div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 90 }}>เลขส่ง</th>
                      <th style={{ width: 110 }}>ลงวันที่</th>
                      <th>ถึง</th>
                      <th>เรื่อง</th>
                      <th style={{ width: 140 }}>ประเภท</th>
                      <th style={{ width: 60 }}>ไฟล์</th>
                      <th style={{ width: 110 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map(d => (
                      <tr key={d.out_id}>
                        <td className="fw-semibold">{d.out_full_no}</td>
                        <td>{fmtDateTH(d.out_doc_date)}</td>
                        <td className="text-truncate" style={{ maxWidth: 160 }} title={d.out_to}>{d.out_to}</td>
                        <td className="text-truncate" style={{ maxWidth: 260 }} title={d.out_subject}>{d.out_subject}</td>
                        <td><Badge bg={d.out_category === 'รายงานต้นสังกัด' ? 'info' : 'secondary'}>{d.out_category}</Badge></td>
                        <td>
                          {d.out_file ? (
                            <a href={`${UPLOAD_BASE}/uploads/ao_docs/${d.out_file}`} target="_blank" rel="noreferrer" title="เปิดไฟล์">
                              <i className="ri-attachment-2" />
                            </a>
                          ) : '-'}
                        </td>
                        <td className="text-end">
                          <Button as={Link} to={`/admin-office/outgoing/${d.out_id}/print`}
                            size="sm" variant="outline-secondary" className="me-1" title="พิมพ์">
                            <i className="ri-printer-line" />
                          </Button>
                          <Button size="sm" variant="outline-primary" title="แก้ไข" onClick={() => openEdit(d)}>
                            <i className="ri-edit-line" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      {/* Create/edit modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {editId ? 'แก้ไขหนังสือส่ง' : 'ออกหนังสือส่ง (ออกเลขอัตโนมัติ)'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Row className="g-2 mb-2">
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">ลงวันที่ <span className="text-danger">*</span></Form.Label>
                <ThaiDateInput size="sm" required value={form.out_doc_date}
                  onChange={v => setForm(f => ({ ...f, out_doc_date: v }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">ประเภทหนังสือ</Form.Label>
                <Form.Select size="sm" value={form.out_category}
                  onChange={e => setForm(f => ({ ...f, out_category: e.target.value }))}>
                  {OUTGOING_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">ผู้ลงนาม</Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} placeholder="เช่น ผู้อำนวยการโรงเรียน" value={form.out_signer}
                  onChange={e => setForm(f => ({ ...f, out_signer: e.target.value }))} />
              </Col>
            </Row>
            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ถึง (หน่วยงาน/บุคคล) <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} required value={form.out_to}
                  onChange={e => setForm(f => ({ ...f, out_to: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">เรื่อง <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" maxLength={500} required value={form.out_subject}
                  onChange={e => setForm(f => ({ ...f, out_subject: e.target.value }))} />
              </Col>
            </Row>
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">
                เนื้อหนังสือ <span className="text-muted">— สำหรับพิมพ์เป็นหนังสือราชการ (ไม่บังคับ)</span>
              </Form.Label>
              <ReactQuill theme="snow" modules={QUILL_MODULES} value={form.out_body}
                onChange={v => setForm(f => ({ ...f, out_body: v }))} style={{ background: '#fff' }} />
            </Form.Group>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">หมายเหตุ <span className="text-muted">— ไม่บังคับ</span></Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} value={form.out_detail}
                  onChange={e => setForm(f => ({ ...f, out_detail: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ไฟล์แนบ (ฉบับลงนาม) <span className="text-muted">— ไม่บังคับ</span></Form.Label>
                <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={e => setFile(e.target.files[0] || null)} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-send-plane-line me-1" />}
              {editId ? 'บันทึกการแก้ไข' : 'ออกหนังสือส่ง'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
